import React from 'react';
import { View, ScrollView } from 'react-native';

import {
  map as _map,
  isNil as _isNil,
  isString as _isString,
  isFunction as _isFunction,
} from 'lodash';
import PropTypes from 'prop-types';

import {
  VFText,
  VFButton,
  QuickActionMenu,
  VFItem,
} from '@vfgroup-oneplatform/foundation/Components';
import { useTheme, getThemeImages } from '@vfgroup-oneplatform/foundation/Components/Themes';

import FrameworkTestKeys from '../../FrameworkTestKeys';
import { Images } from '../../Themes';

import Styles from './SwitchAccount.Styles';

const getPropRightIcon = (activeIcon, inactiveIcon, theme, images, isActive) => {
  if (isActive && _isNil(activeIcon)) {
    return Images.ic_tick_solution;
  } else if (isActive) {
    return _isString(activeIcon) ? images[activeIcon] : activeIcon;
  } else if (inactiveIcon) {
    return _isString(inactiveIcon) ? images[inactiveIcon] : inactiveIcon;
  }

  return getThemeImages(theme.name).ic_chevron_right_red;
};

const SwitchAccount = ({
  isVisible,
  onClosePress,
  onCancelPress,
  title,
  currentSectionTitle,
  otherSectionTitle,
  onAccountPress,
  renderCustomSection,
  accounts,
  currentAccount,
  images,
  activeAccountIcon,
  inactiveAccountIcon,
  onModalWillShow,
  onModalWillHide,
  onModalHide,
  onModalShow,
  renderContent,
}) => {
  const theme = useTheme();

  return (
    <QuickActionMenu
      isVisible={isVisible}
      type="secondary"
      title={title}
      containerStyle={Styles.quickActionMenuContainerStyle}
      toggleModal={onClosePress}
      onModalWillShow={onModalWillShow}
      onModalWillHide={onModalWillHide}
      onModalHide={onModalHide}
      onModalShow={onModalShow}
      mainViewAccessibilityLabel={FrameworkTestKeys.SwitchAccountQuickActionMainView}
      TitleAccessibilityLabel={FrameworkTestKeys.SwitchAccountTitle}
      closeIconAccessibilityLabel={FrameworkTestKeys.SwitchAccountCloseIcon}
    >
      {_isFunction(renderContent) ? (
        renderContent()
      ) : (
        <View style={Styles.containerStyle} testID={FrameworkTestKeys.SwitchAccountView}>
          <ScrollView>
            <VFText
              i18nKey={currentSectionTitle}
              style={Styles.currentAccountTitleStyle(theme)}
              testKey={FrameworkTestKeys.SwitchAccountCurrentAccountTitle}
            />

            <VFItem
              containerStyle={Styles.accountItemContainerStyle(theme)}
              {...currentAccount}
              disabled
              onPress={onAccountPress}
              rightIcon={getPropRightIcon(
                activeAccountIcon,
                inactiveAccountIcon,
                theme,
                images,
                true,
              )}
              leftIcon={images[currentAccount.icon]}
            />
            {_isFunction(renderCustomSection) && renderCustomSection()}
            <VFText
              i18nKey={otherSectionTitle}
              style={Styles.otherAccountsTitleStyle(theme)}
              testKey={FrameworkTestKeys.SwitchAccountOtherAccountsTitle}
            />

            {_map(accounts, (account, index) => {
              return (
                <VFItem
                  key={account.id}
                  containerStyle={index === 0 ? Styles.accountItemContainerStyle(theme) : undefined}
                  {...account}
                  onPress={onAccountPress}
                  itemTestID={FrameworkTestKeys.AccountItemTestID}
                  rightIcon={getPropRightIcon('', inactiveAccountIcon, theme, images, false)}
                  leftIcon={images[account.icon]}
                />
              );
            })}
          </ScrollView>

          <VFButton
            title="switch_account_quick_action_cancel_button_title"
            onPress={onCancelPress}
            type="secondary"
            style={Styles.cancelButtonStyle(theme)}
            testKey={FrameworkTestKeys.SwitchAccountCancelCTA}
          />
        </View>
      )}
    </QuickActionMenu>
  );
};

SwitchAccount.defaultProps = {
  title: 'switch_account_quick_action_title',
  currentSectionTitle: 'switch_account_quick_action_current_account_title',
  otherSectionTitle: 'switch_account_quick_action_other_accounts_title',
};

SwitchAccount.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onClosePress: PropTypes.func.isRequired,
  onCancelPress: PropTypes.func.isRequired,
  onAccountPress: PropTypes.func.isRequired,
  title: PropTypes.string,
  currentSectionTitle: PropTypes.string,
  otherSectionTitle: PropTypes.string,
  renderCustomSection: PropTypes.func,
  accounts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      icon: PropTypes.string.isRequired,
    }),
  ).isRequired,
  currentAccount: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    icon: PropTypes.string,
  }).isRequired,
  images: PropTypes.object,
  activeAccountIcon: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  inactiveAccountIcon: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onModalWillShow: PropTypes.func,
  onModalWillHide: PropTypes.func,
  onModalHide: PropTypes.func,
  onModalShow: PropTypes.func,
  renderContent: PropTypes.func,
};

export default SwitchAccount;
